import React, { useState, useRef } from "react";
import {
  AppBar,
  Box,
  CardMedia,
  Dialog,
  DialogContent,
  Divider,
  Stack,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { StepScreen } from "./StepScreen";
import { EditQnA } from "./EditQnA";
import { EmptyQnA } from "./EmptyQnA";
import { EGIconButton } from "../../../../../MainComponents/Button/IconButton/IconButton";
import { EGExpandingButton } from "../../../../../MainComponents/Button/Expanding/EGExpandingButton";
import { InterviewActions } from "./InterviewActions";
import { ApplyQnA } from "./ApplyQnA";
import { FinishConfirm } from "./FinishConfirm";
import { Evaluation } from "./Evaluation/Evaluation";
import { updateInterview } from "../../../../../../apis";
import EGLogoMobil from "../../../../../../assets/MainLogo/Isotipo_EG.svg";
import EGLogo from "../../../../../../assets/MainLogo/Imagotipo_EG.svg";
import { useAudioRecorder } from "../../../../../../hooks/useAudioRecorder";
import "./InterviewQnA.scss";

export const InterviewQnA = ({
  show,
  onClose,
  interview = {},
  id,
  alias,
  setInterviews,
  summaryMode = false,
}) => {
  const theme = useTheme();
  const [t] = useTranslation("investigation");
  const isMobil = useMediaQuery(theme.breakpoints.down("md"));

  const [step, setStep] = useState("home");
  const [isLoading, setIsLoading] = useState(false);
  const [openFinalizeDialog, setOpenFinalizeDialog] = useState(false);
  // En summaryMode arrancamos en "interview" porque la tab "analysis" se
  // oculta si no hay Q&A.
  const [tabValue, setTabValue] = useState(
    summaryMode ? "interview" : "analysis",
  );

  // En summaryMode siempre usamos el look de "finish" (gris secondary)
  // independientemente del status real de la entrevista.
  const useFinishLook = summaryMode || interview.status === "finish";
  const primaryMain = useFinishLook
    ? theme.palette.secondary.main
    : theme.palette.primary.main;
  const primaryDark = useFinishLook
    ? theme.palette.secondary.dark
    : theme.palette.primary.dark;

  const editRef = useRef(null);
  const applyRef = useRef(null);
  const evaluationRef = useRef(null);

  // Audio Recording Hook
  const audioRecorder = useAudioRecorder(id, alias);

  // Destructure audio states for reactive updates in buttons
  const {
    isRecording: audioIsRecording,
    isPaused: audioIsPaused,
    duration: audioDuration,
    isSupported: audioIsSupported,
    toggleRecording: audioToggleRecording,
    stopRecording: audioStopRecording,
    formatDuration: audioFormatDuration,
  } = audioRecorder;

  // handlers
  const handleHome = () => setStep("home");
  const handleEdit = () => setStep("edit");
  const handleApply = () => setStep("apply");

  const handleEvaluate = () => setStep("evaluation");

  // Finaliza la entrevista directamente (sin pasar por preguntas/respuestas) y
  // la deja lista para la evaluación: carga de evidencias, análisis y conclusión.
  // Conserva las preguntas y adjuntos que ya existieran.
  const handleFinalize = async () => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      const resp = await updateInterview({
        alias,
        interviews_id: id,
        interviews_data: {
          status: "finish",
          interview: interview?.interview || [],
          ...(interview?.attachments?.length > 0 && {
            attachments: interview.attachments,
          }),
        },
      });
      setInterviews(resp?.data?.interviews);
      setStep("home");
    } catch (error) {
      console.error("Error al finalizar la entrevista:", error);
    } finally {
      setIsLoading(false);
      setOpenFinalizeDialog(false);
    }
  };

  const stepTitles = {
    home: t("Interview.interview_cuestions_answers"),
    edit: t("Interview.edit_interview"),
    apply: t("Interview.conduct_interview"),
    evaluation: t("Interview.evaluation"),
  };

  // Screen settings
  const stepsConfig = [
    {
      name: "home",
      content: (
        <Box
          sx={{
            flex: 1,
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            position: "relative",
            background:
              summaryMode ||
              interview.status === "finish" ||
              interview?.interview?.length > 0
                ? `linear-gradient(180deg, ${primaryMain} 0%, ${primaryDark} 100%)`
                : "",
          }}
        >
          {/* Close InterviewQnA */}
          <Stack
            sx={{
              position: "absolute",
              left: 20,
              bottom: 25,
              zIndex: theme.zIndex.modal + 2,
            }}
            spacing={2}
          >
            {!summaryMode && tabValue === "attachments" && (
              <EGExpandingButton
                label={t("Interview.add_file")}
                labelColor="white"
                iconName="add"
                iconColor="white"
                iconSize={30}
                buttonColor="green"
                onClick={() => evaluationRef.current?.handleAddNewFile?.()}
              />
            )}
            {!summaryMode &&
              tabValue === "analysis" &&
              interview?.evaluation &&
              interview?.conclusion == null && (
                <EGExpandingButton
                  label={t("Interview.add_interview_conclusion")}
                  labelColor="white"
                  iconName="receipt_edit"
                  iconColor="white"
                  iconSize={30}
                  buttonColor="orange"
                  onClick={() => evaluationRef.current?.handleAddConclusion?.()}
                />
              )}
            <EGExpandingButton
              label={t("Interview.back")}
              labelColor="deepIndigo"
              buttonColor="white"
              iconColor="deepIndigo"
              onClick={
                !summaryMode && interview.status === "finish"
                  ? () => evaluationRef.current?.handleGoHome?.()
                  : () => onClose()
              }
              iconName="outline_logout"
              sx={{
                boxShadow: `1px 1px 7px -2px ${theme.palette.blueGray700.main}`,
              }}
            />
          </Stack>

          {summaryMode ? (
            <Evaluation
              ref={evaluationRef}
              interview={interview}
              setInterviews={setInterviews}
              alias={alias}
              isLoading={isLoading}
              setIsLoading={setIsLoading}
              goHome={onClose}
              isMobil={isMobil}
              tabValue={tabValue}
              setTabValue={setTabValue}
              readOnly
            />
          ) : interview.status === "finish" ? (
            // ----> Evaluation <---- //
            // Una entrevista finalizada va siempre a la evaluación (carga de
            // evidencias, análisis, conclusión), aunque no tenga preguntas.
            <Evaluation
              ref={evaluationRef}
              interview={interview}
              setInterviews={setInterviews}
              alias={alias}
              isLoading={isLoading}
              setIsLoading={setIsLoading}
              goHome={onClose}
              isMobil={isMobil}
              tabValue={tabValue}
              setTabValue={setTabValue}
            />
          ) : interview?.interview?.length > 0 ? (
            // Edit and apply interview actions
            <InterviewActions
              onEdit={handleEdit}
              onApply={handleApply}
              onEvaluate={handleEvaluate}
              status={interview.status}
            />
          ) : (
            // Start setting up interview
            <EmptyQnA
              onClose={onClose}
              onEdit={handleEdit}
              onApply={handleApply}
              onFinalize={() => setOpenFinalizeDialog(true)}
              isMobil={isMobil}
              step={step}
            />
          )}
        </Box>
      ),
    },
    {
      name: "edit",
      content: (
        <EditQnA
          ref={editRef}
          goHome={handleHome}
          questionnaire={interview?.interview || []}
          interviewed={interview?.interviewed || {}}
          isMobil={isMobil}
          id={id}
          alias={alias}
          isLoading={isLoading}
          setIsLoading={setIsLoading}
          setInterviews={setInterviews}
        />
      ),
      // Mobile Menu
      actions: isMobil ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            p: 1.5,
            borderTop: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.paper,
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <EGIconButton
              title={t("Interview.save")}
              iconName="save"
              iconColor="success"
              onClick={(e) => {
                e.stopPropagation();
                editRef.current?.handleSave?.();
              }}
              disabled={isLoading}
            />
            <EGIconButton
              title={t("Interview.skip_to_interview")}
              iconName="outline_message_text_2"
              iconColor="cobalt"
              onClick={(e) => {
                e.stopPropagation();
                handleApply();
              }}
              disabled={isLoading}
            />
            <EGIconButton
              title={t("Interview.finish_and_upload_evidence")}
              iconName="receipt_edit"
              iconColor="orange"
              onClick={(e) => {
                e.stopPropagation();
                setOpenFinalizeDialog(true);
              }}
              disabled={isLoading}
            />
            <EGIconButton
              title={t("Interview.cancel")}
              iconName="cancel_circle"
              iconColor="error"
              onClick={(e) => {
                e.stopPropagation();
                editRef.current?.handleCancel?.();
              }}
              disabled={isLoading}
            />
          </Stack>
        </Box>
      ) : (
        // desktop: vertical buttons
        <Box sx={{ position: "relative" }}>
          <Stack
            direction="column"
            spacing={1}
            sx={{
              position: "absolute",
              left: 20,
              bottom: 25,
              zIndex: (theme) => theme.zIndex.modal,
            }}
          >
            <EGExpandingButton
              label={t("Interview.save")}
              labelColor="white"
              buttonColor="success"
              buttonColorHover="success"
              iconColor="white"
              onClick={() => {
                editRef.current?.handleSave?.();
              }}
              loading={isLoading}
              iconName="save"
            />
            <EGExpandingButton
              label={t("Interview.skip_to_interview")}
              buttonColor="white"
              buttonColorHover="blueGray50"
              iconColor="cobalt"
              labelColor="cobalt"
              onClick={() => handleApply()}
              loading={isLoading}
              iconName="outline_message_text_2"
            />
            <EGExpandingButton
              label={t("Interview.finish_and_upload_evidence")}
              buttonColor="white"
              buttonColorHover="blueGray50"
              iconColor="orange"
              labelColor="orange"
              onClick={() => setOpenFinalizeDialog(true)}
              loading={isLoading}
              iconName="receipt_edit"
            />
            <EGExpandingButton
              label={t("Interview.cancel")}
              buttonColor="white"
              buttonColorHover="blueGray50"
              iconColor="error"
              onClick={() => {
                editRef.current?.handleCancel?.();
              }}
              loading={isLoading}
              iconName="close_icon"
            />
          </Stack>
        </Box>
      ),
    },
    {
      name: "apply",
      content: (
        <ApplyQnA
          ref={applyRef}
          goHome={handleHome}
          interview={interview || []}
          evidence={interview.attachments}
          isMobil={isMobil}
          id={id}
          alias={alias}
          isLoading={isLoading}
          setIsLoading={setIsLoading}
          setInterviews={setInterviews}
          audioRecorder={audioRecorder}
        />
      ),
      actions: isMobil ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            p: 1.5,
            borderTop: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.paper,
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <EGIconButton
              title={t("Interview.add_question")}
              iconColor="success"
              iconName="outline_message_add"
              disabled={isLoading}
              onClick={(e) => {
                e.stopPropagation();
                applyRef.current?.addNewQuestion?.();
              }}
            />
            {audioIsSupported && (
              <>
                {/* Start/Resume/Pause Recording */}
                <EGIconButton
                  title={
                    audioIsRecording
                      ? audioIsPaused
                        ? t("Interview.resume_recording")
                        : t("Interview.pause_recording")
                      : t("Interview.start_recording")
                  }
                  iconColor={
                    audioIsRecording
                      ? audioIsPaused
                        ? "success"
                        : "warning"
                      : "secondary"
                  }
                  iconName={
                    audioIsRecording
                      ? audioIsPaused
                        ? "bold_play_circle_3"
                        : "pause_circle"
                      : "microphone_2"
                  }
                  disabled={isLoading}
                  onClick={() => audioToggleRecording?.()}
                />
                {/* Stop Recording (only show when recording) */}
                {audioIsRecording && (
                  <EGIconButton
                    title={t("Interview.stop_recording")}
                    iconColor="error"
                    iconName="bold_stop_circle_2"
                    disabled={isLoading}
                    onClick={() => audioStopRecording?.(true)}
                  />
                )}
              </>
            )}
            <Divider sx={{ height: 20, mx: 1.5 }} orientation="vertical" />
            <EGIconButton
              title={t("Interview.pause_and_save_progress")}
              iconColor="info"
              onClick={() => applyRef.current?.saveAsProcess?.()}
              disabled={isLoading}
              iconName="pause"
            />
            <EGIconButton
              title={t("Interview.finish_interview")}
              iconColor="red"
              onClick={() => applyRef.current.openFinishModal()}
              disabled={isLoading}
              iconName="save"
            />
            <EGIconButton
              title={t("Interview.cancel")}
              iconColor="secondary"
              onClick={() => applyRef.current?.handleGoHome?.()}
              disabled={isLoading}
              iconName="cancel_circle"
            />
          </Stack>
        </Box>
      ) : (
        <Box sx={{ position: "relative" }}>
          <Stack
            direction="column"
            spacing={1}
            sx={{
              position: "absolute",
              left: 20,
              bottom: 25,
              zIndex: (theme) => theme.zIndex.modal,
            }}
          >
            <EGExpandingButton
              label={t("Interview.add_question")}
              buttonColor="success.dark"
              buttonColorHover="success.dark"
              iconColor="white"
              labelColor="white"
              onClick={() => applyRef.current?.addNewQuestion?.()}
              loading={isLoading}
              iconName="outline_message_add"
            />
            {audioIsSupported && (
              <>
                {/* Start/Resume/Pause Recording */}
                <EGExpandingButton
                  label={
                    audioIsRecording
                      ? `${audioFormatDuration?.(audioDuration || 0)} - ${
                          audioIsPaused
                            ? t("Interview.resume_recording")
                            : t("Interview.pause_recording")
                        }`
                      : t("Interview.start_recording")
                  }
                  buttonColor={
                    audioIsRecording
                      ? audioIsPaused
                        ? "success"
                        : "warning"
                      : "secondary"
                  }
                  buttonColorHover={
                    audioIsRecording
                      ? audioIsPaused
                        ? "success"
                        : "warning"
                      : "secondary"
                  }
                  iconColor="white"
                  labelColor="white"
                  onClick={() => audioToggleRecording?.()}
                  loading={isLoading}
                  iconName={
                    audioIsRecording
                      ? audioIsPaused
                        ? "bold_play_circle_2"
                        : "pause_circle"
                      : "microphone_2"
                  }
                />
                {/* Stop Recording (only show when recording) */}
                {audioIsRecording && (
                  <EGExpandingButton
                    label={t("Interview.stop_recording")}
                    buttonColor="error"
                    buttonColorHover="error"
                    iconColor="white"
                    labelColor="white"
                    onClick={() => audioStopRecording?.(true)}
                    loading={isLoading}
                    iconName="stop_circle_2"
                  />
                )}
              </>
            )}
            <EGExpandingButton
              label={t("Interview.pause_and_save_progress")}
              buttonColor="info"
              buttonColorHover="info"
              iconColor="white"
              onClick={() => applyRef.current?.saveAsProcess?.()}
              loading={isLoading}
              iconName="pause"
            />
            <EGExpandingButton
              label={t("Interview.finish_interview")}
              buttonColor="red"
              labelColor="white"
              iconColor="white"
              onClick={() => applyRef.current.openFinishModal()}
              loading={isLoading}
              iconName="save"
            />
            <EGExpandingButton
              label={t("Interview.cancel")}
              buttonColor="white"
              buttonColorHover="blueGray50"
              iconColor="error"
              onClick={() => applyRef.current?.handleGoHome?.()}
              loading={isLoading}
              iconName="close_icon"
            />
          </Stack>
        </Box>
      ),
    },
  ];

  return (
    <Dialog fullScreen open={show} keepMounted={false}>
      {/* Header */}
      <AppBar
        elevation={useFinishLook ? 0 : 2}
        sx={{
          position: "relative",
          backgroundColor: theme.palette.common.white,
        }}
      >
        <Toolbar>
          <CardMedia
            component="img"
            image={isMobil ? EGLogoMobil : EGLogo}
            sx={{
              position: isMobil ? undefined : "absolute",
              left: 40,
              top: "50%",
              m: isMobil ? "auto" : "",
              transform: isMobil ? "" : "translateY(-50%)",
              maxHeight: isMobil ? 30 : 50,
              maxWidth: isMobil ? 30 : 175,
              userSelect: "none",
              WebkitUserSelect: "none",
              MozUserSelect: "none",
              msUserSelect: "none",
              WebkitTouchCallout: "none",
              WebkitUserDrag: "none",
            }}
            alt="Logo"
          />
          {/* Main title */}
          <Typography
            sx={{ flex: 1 }}
            fontSize={{ xs: 14, md: 18 }}
            fontWeight={700}
            textAlign="center"
            color="secondary"
          >
            {stepTitles[step] || ""}
          </Typography>
        </Toolbar>
      </AppBar>
      {/* Dynamic render screen */}
      <DialogContent>
        {stepsConfig.map(({ name, content, actions }) => (
          <StepScreen
            key={name}
            name={name}
            activeStep={step}
            actions={actions}
          >
            {content}
          </StepScreen>
        ))}
      </DialogContent>

      {/* Finalizar directamente e ir a la evaluación (carga de evidencias) */}
      <FinishConfirm
        open={openFinalizeDialog}
        onClose={() => setOpenFinalizeDialog(false)}
        onCancel={() => setOpenFinalizeDialog(false)}
        onConfirm={handleFinalize}
        isMobil={isMobil}
        isLoading={isLoading}
      />
    </Dialog>
  );
};
